import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { generateResponse, QUICK_PROMPTS, resetStrategySession, getStrategySessionInfo } from '../../lib/sensoryStrategiesChat';
import { MessageCircle, X } from 'lucide-react';

type Message = { role: 'user' | 'assistant'; content: string };

const WELCOME_HTML = `<p>I'm your <strong>Sensory & Speech Strategies Assistant</strong>. Describe any sensory challenge, speech difficulty, or communication situation and I'll suggest practical strategies (no AI, rule-based).</p><p>If these ideas aren't enough, you can ask to <strong>connect with a therapist</strong> using the button below.</p>`;

export function HelpChatOverlay() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: WELCOME_HTML },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionTurns, setSessionTurns] = useState(() => getStrategySessionInfo().turns);
  const [sessionMaxTurns, setSessionMaxTurns] = useState(() => getStrategySessionInfo().maxTurns);
  const [isListening, setIsListening] = useState(false);
  const [speechStatus, setSpeechStatus] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const isParent = isAuthenticated && (user?.role === 'PARENT' || user?.role === 'CAREGIVER');
  const hideFab =
    location.pathname === '/therapist-marketplace' ||
    location.pathname === '/messages' ||
    location.pathname.startsWith('/messages/');

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      inputRef.current?.focus();
    }
  }, [open, messages]);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open]);

  const sendMessage = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setInput('');
    setSending(true);

    setTimeout(() => {
      const responseHtml = generateResponse(trimmed);
      const info = getStrategySessionInfo();
      setSessionTurns(info.turns);
      setSessionMaxTurns(info.maxTurns);
      setMessages((prev) => [...prev, { role: 'assistant', content: responseHtml }]);
      setSending(false);
    }, 200);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleContactTherapist = () => {
    setOpen(false);
    navigate('/therapist-marketplace');
  };

  const handleResetSession = () => {
    resetStrategySession();
    const info = getStrategySessionInfo();
    setSessionTurns(info.turns);
    setSessionMaxTurns(info.maxTurns);
    setMessages([{ role: 'assistant', content: WELCOME_HTML }]);
  };

  const handleMicClick = () => {
    if (sending || isListening) return;
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSpeechStatus('Speech recognition not available in this browser.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    let finalTranscript = '';

    recognition.onstart = () => {
      setIsListening(true);
      setSpeechStatus('Listening…');
    };
    recognition.onerror = () => {
      setIsListening(false);
      setSpeechStatus('Mic error. Try again or type instead.');
    };
    recognition.onend = () => {
      setIsListening(false);
      setSpeechStatus(null);
      if (finalTranscript.trim()) {
        setInput(finalTranscript.trim());
        sendMessage(finalTranscript.trim());
      }
    };
    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalTranscript += t;
        else interim += t;
      }
      if (interim) {
        setInput(interim);
        setSpeechStatus(`Hearing: "${interim}"`);
      }
    };

    recognition.start();
  };

  if (!isParent) return null;

  return (
    <>
      {/* FAB - only when not on Find a therapist (/therapist-marketplace) */}
      {!hideFab && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-4 z-40 flex items-center gap-2 rounded-full bg-indigo-600 p-3 text-white shadow-lg transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:right-6 sm:px-4 sm:py-3"
          aria-label="I need help – open sensory strategies chat"
        >
          <MessageCircle className="h-5 w-5" />
          <span className="hidden font-medium sm:inline">I need help</span>
        </button>
      )}

      {/* Overlay – anchored chat panel in bottom-right */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-end p-2 sm:p-4 md:p-6 pointer-events-none">
          <div
            className="pointer-events-auto flex h-[520px] w-full max-w-3xl flex-col rounded-2xl border border-slate-700 bg-slate-900 text-slate-100 shadow-2xl md:h-[640px]"
            style={{ color: 'var(--chat-text, #e2e8f0)' }}
          >
            <div className="flex items-center justify-between border-b border-slate-700 bg-slate-800/80 px-4 py-3">
              <div>
                <h2 className="text-lg font-semibold">Sensory &amp; Speech Strategies Assistant</h2>
                <p className="mt-0.5 text-xs text-slate-400">
                  Practical support for sensory &amp; communication challenges
                </p>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-300">
                <button
                  type="button"
                  onClick={handleResetSession}
                  className="rounded-full border border-slate-500 px-2 py-1 text-[0.7rem] uppercase tracking-wide hover:bg-slate-700"
                >
                  Reset
                </button>
                <span className="text-[0.7rem]">
                  {sessionTurns}/{sessionMaxTurns}
                </span>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded p-2 text-slate-400 hover:bg-slate-700 hover:text-white"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {speechStatus && (
                <div className="text-xs text-slate-300 mb-1">{speechStatus}</div>
              )}
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[90%] rounded-2xl px-4 py-2 ${
                      msg.role === 'user'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-700/80 text-slate-100'
                    }`}
                  >
                    {msg.role === 'user' ? (
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    ) : (
                      <div className="help-chat-html text-sm leading-relaxed">
                        <div dangerouslySetInnerHTML={{ __html: msg.content }} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="rounded-2xl bg-slate-700/80 px-4 py-2 text-slate-400">
                    Thinking…
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="border-t border-slate-700 bg-slate-800/90 p-3 md:p-4">
              <div className="mb-2 flex flex-wrap gap-2">
                {QUICK_PROMPTS.map(({ label, prompt }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => sendMessage(prompt)}
                    disabled={sending}
                    className="rounded-full bg-slate-600 px-3 py-1.5 text-xs text-slate-200 transition hover:bg-slate-500 disabled:opacity-50"
                  >
                    {label}
                  </button>
                ))}
              </div>
              <form onSubmit={handleSubmit} className="flex gap-2 pt-1 items-end">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Describe what's going on..."
                  rows={2}
                  className="flex-1 resize-none rounded-xl border border-slate-600 bg-slate-700 px-3 py-2 text-slate-100 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  disabled={sending || isListening}
                />
                <button
                  type="button"
                  onClick={handleMicClick}
                  disabled={sending || isListening}
                  className="mb-1 rounded-xl border border-slate-500 bg-slate-800 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-200 hover:bg-slate-700 disabled:opacity-50"
                >
                  {isListening ? 'Listening…' : 'Mic'}
                </button>
                <button
                  type="submit"
                  disabled={sending || !input.trim()}
                  className="self-end rounded-xl bg-indigo-600 px-4 py-2 font-medium text-white transition hover:bg-indigo-500 disabled:opacity-50"
                >
                  Send
                </button>
              </form>

              <div className="mt-3 flex justify-center">
                <button
                  type="button"
                  onClick={handleContactTherapist}
                  className="rounded-lg border border-indigo-500 bg-indigo-600/20 px-4 py-2 text-sm font-medium text-indigo-300 transition hover:bg-indigo-600/40"
                >
                  Contact a therapist
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .help-chat-html { max-width: 52rem; }
        .help-chat-html p {
          margin: 0 0 0.75rem;
        }
        .help-chat-html .strategy-section {
          margin-top: 1.2rem;
          padding-top: 0.25rem;
          border-top: 1px solid rgba(148, 163, 184, 0.35);
        }
        .help-chat-html .strategy-section h4 {
          font-size: 0.95rem;
          margin-bottom: 0.5rem;
          color: #c7d2fe;
        }
        .help-chat-html ul {
          list-style: none;
          padding-left: 0;
          margin: 0.25rem 0 0.1rem;
        }
        .help-chat-html li {
          margin: 0.4rem 0;
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
        }
        .help-chat-html .category-tag {
          font-size: 0.7rem;
          background: rgba(99, 102, 241, 0.35);
          padding: 0.1rem 0.45rem;
          border-radius: 999px;
          margin-top: 0.05rem;
          flex-shrink: 0;
          white-space: nowrap;
        }
      `}</style>
    </>
  );
}
